import { Droplet, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { POLICY_LINKS } from "@/data/policies";
import { useBusiness } from "@/lib/useBusiness";

export default function Footer() {
  const biz = useBusiness();
  return (
    <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
          <div>
            <button
              onClick={() => scrollToId("#top")}
              className="flex items-center gap-2"
              data-testid="footer-logo"
            >
              <span className="w-7 h-7 bg-slate-950 text-white flex items-center justify-center rounded-sm">
                <Droplet className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <span className="font-chivo font-extrabold tracking-tight text-lg">
                LAB<span className="text-slate-400">2O</span>
              </span>
            </button>
            <p className="mt-4 font-plexmono text-[10px] tracking-[0.25em] uppercase text-slate-400">
              Research-grade bacteriostatic water
            </p>
          </div>

          <p className="max-w-xl text-xs text-slate-500 leading-relaxed" data-testid="footer-legal-disclaimer">
            All Lab2o products are supplied strictly as laboratory research
            reagents. For research purposes only. Not for human or veterinary
            use or consumption. No medical, therapeutic, diagnostic, or clinical
            claims are made or implied. The buyer assumes all responsibility for
            the safe handling, storage, and lawful use of these products in a
            research setting. By ordering, the buyer confirms that products will
            be used solely for legitimate laboratory research.
          </p>
        </div>

        <div className="mt-12 pt-10 border-t border-slate-100 grid sm:grid-cols-2 gap-10">
          <div data-testid="footer-contact">
            <p className="font-plexmono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-4">Contact</p>
            <div className="text-sm text-slate-600 flex flex-col gap-2.5">
              <span className="font-medium text-slate-700" data-testid="footer-business-name">Lab2o</span>
              <span className="flex items-center gap-2.5" data-testid="footer-email">
                <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                {biz.email}
              </span>
            </div>
          </div>
          <div data-testid="footer-policies">
            <p className="font-plexmono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-4">Policies</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              <li>
                <Link to="/bacteriostatic-water" className="text-sm text-slate-600 hover:text-slate-950 transition-colors" data-testid="footer-bacteriostatic-water">
                  Bacteriostatic Water
                </Link>
              </li>
              <li>
                <Link to="/wholesale" className="text-sm text-slate-600 hover:text-slate-950 transition-colors" data-testid="footer-wholesale">
                  Wholesale
                </Link>
              </li>
              <li>
                <Link to="/shipping" className="text-sm text-slate-600 hover:text-slate-950 transition-colors" data-testid="footer-shipping">
                  Shipping
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-slate-600 hover:text-slate-950 transition-colors" data-testid="footer-faq">
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/lab-results"
                  className="text-sm text-slate-600 hover:text-slate-950 transition-colors"
                  data-testid="footer-lab-results"
                >
                  Lab Results
                </Link>
              </li>
              {POLICY_LINKS.map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/policies/${l.slug}`}
                    className="text-sm text-slate-600 hover:text-slate-950 transition-colors"
                    data-testid={`footer-policy-${l.slug}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-400" data-testid="footer-copyright">
            © 2026 Lab2o{" "}
            <Link to="/admin" className="underline hover:text-slate-600" data-testid="footer-admin-link">Admin</Link>
          </p>
          <p className="font-plexmono text-[10px] tracking-[0.25em] uppercase text-red-600" data-testid="footer-research-only">
            For research purposes only
          </p>
        </div>
      </div>
    </footer>
  );
}
